import { IconName, IconPrefix } from "@fortawesome/fontawesome-common-types";
import { Content } from "@iventis/translations/content/typed-content";
import { AppBarSubmenuItem, StyledAppBarContainer, AppBarSubmenu } from "@iventis/components";
import React, { PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";
import { Switch, useLocation, Redirect, useRouteMatch } from "react-router-dom";
import { useHistory } from "@iventis/subscriptions";

import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { IventisErrorBoundaryWithToast } from "@iventis/error-boundaries";
import { toast } from "@iventis/toasts/src/toast";
import { TeamsView } from "@iventis/people-app/src/teams-view";
import { PeopleSubmenuOptions } from "@iventis/people-app/src/people.types";
import { peopleRoute, teamsRoute } from "@iventis/people-app/src/constants";
import { PeopleView } from "@iventis/people-app/src/people-view";
import { AppSubBarMenuContext } from "@iventis/utilities/src/app-bar-submenu-context";
import { IventisRoute as Route } from "@iventis/permissions/src/iventis-route";

const PeoplePage: React.FC<PropsWithChildren> = ({ children }) => {
    const { path } = useRouteMatch();

    const history = useHistory();
    const { pathname } = useLocation();

    const translate = useIventisTranslate();

    const appSubmenuOptions = useMemo(
        () => [
            {
                id: PeopleSubmenuOptions.People,
                name: translate(Content.people.people),
                pageIcon: { prefix: "fas" as IconPrefix, iconName: "users" as IconName },
                sidePanelIcon: { prefix: "fas" as IconPrefix, iconName: "users" as IconName },
            },
            {
                id: PeopleSubmenuOptions.Teams,
                name: translate(Content.people.teams),
                pageIcon: { prefix: "fas" as IconPrefix, iconName: "screen-users" as IconName },
                sidePanelIcon: { prefix: "fas" as IconPrefix, iconName: "screen-users" as IconName },
            },
        ],
        [translate]
    );

    useEffect(() => {
        if (pathname === teamsRoute) {
            setSelectedSubmenuOption(PeopleSubmenuOptions.Teams);
        }

        if (pathname === peopleRoute) {
            setSelectedSubmenuOption(PeopleSubmenuOptions.People);
        }
    }, [pathname]);

    const [selectedSubmenuOption, setSelectedSubmenuOption] = useState(appSubmenuOptions[0].id);

    const { mobileDrawerOpen, setMobileDrawerOpen } = useContext(AppSubBarMenuContext);

    const submenuItemSelected = (item: AppBarSubmenuItem) => {
        setMobileDrawerOpen(false);

        if (item.id === PeopleSubmenuOptions.People) {
            history.push(peopleRoute);
        }

        if (item.id === PeopleSubmenuOptions.Teams) {
            history.push(teamsRoute);
        }
    };

    return (
        <IventisErrorBoundaryWithToast toast={toast}>
            <StyledAppBarContainer>
                <AppBarSubmenu
                    items={appSubmenuOptions}
                    mobileOpen={mobileDrawerOpen}
                    fullScreenTitle="People"
                    selectedItem={selectedSubmenuOption}
                    onItemClick={submenuItemSelected}
                />
                <Switch>
                    {/* Remove trailing slashes */}
                    <Redirect from="/:url*(/+)" to={pathname.slice(0, -1)} />
                    <Route
                        exact
                        path={`${path}/teams`}
                        render={() => (
                            <IventisErrorBoundaryWithToast toast={toast}>
                                <TeamsView />
                            </IventisErrorBoundaryWithToast>
                        )}
                    />
                    <Route
                        exact
                        path={path}
                        render={() => (
                            <IventisErrorBoundaryWithToast toast={toast}>
                                <PeopleView />
                            </IventisErrorBoundaryWithToast>
                        )}
                    />
                </Switch>
                {children}
            </StyledAppBarContainer>
        </IventisErrorBoundaryWithToast>
    );
};

export default PeoplePage;
